"use client";

import { useEffect, useMemo } from "react";

import type { Direction } from "@repo/ui/constants/directions";
import { SSR_CODES, SSR_CODES_CHECKIN } from "@repo/ui/constants/ssrs";
import { findSSRWithPurchasePriority } from "@repo/ui/helpers/Extras/findPurchasedSsr";
import { isSsrIncluded } from "@repo/ui/helpers/Extras/isSsrIncluded";
import type { PassengerSegment } from "@repo/ui/types/booking";
import type { CommonInfoSection, PriorityBoardingSection } from "@repo/ui/types/cms";
import type { Passenger } from "@repo/ui/types/manageBooking";

import { useAppDispatch } from "@repo/store/hooks";
import { setPriorityBoardingButtonStatus, useIsCheckin } from "@repo/store/slices";

import FlightSegmentCard from "../common/FlightSegmentCard";
import { PassengerItemCard } from "../common/PassengerItemCard";

type ExtrasPriorityBoardingPassCardProps = {
  direction: Direction;
  directionLabel?: string;
  options: Record<string, PassengerSegment>;
  onChangeAction: (
    passengerKey: string,
    price: string | number,
    ssrKey?: string,
    ssrCode?: string,
  ) => void;
  addedPriorityBoarding: Record<string, boolean>;
  bundleCode?: string;
  cmsData?: PriorityBoardingSection;
  commonInfo?: CommonInfoSection;
  passengers?: Passenger[];
  loadingKeys?: string[];
  isJourneyDisabled?: boolean;
};

export default function ExtrasPriorityBoardingPassCard({
  direction,
  directionLabel,
  options,
  onChangeAction,
  addedPriorityBoarding,
  bundleCode,
  cmsData,
  commonInfo,
  passengers,
  loadingKeys,
  isJourneyDisabled = false,
}: Readonly<ExtrasPriorityBoardingPassCardProps>) {
  const dispatch = useAppDispatch();
  const isCheckIn = useIsCheckin();

  const priorityBoardingCode = useMemo(
    () => (isCheckIn ? SSR_CODES_CHECKIN.PRIORITY_BOARDING : SSR_CODES.PRIORITY_BOARDING),
    [isCheckIn],
  );

  const priorityBoardingCodes = useMemo(
    () =>
      isCheckIn
        ? [SSR_CODES_CHECKIN.PRIORITY_BOARDING, SSR_CODES.PRIORITY_BOARDING]
        : [SSR_CODES.PRIORITY_BOARDING],
    [isCheckIn],
  );

  const isIncludedByBundle = useMemo(() => {
    if (!direction) return false;

    return Object.values(options).some((option) => {
      const ssr = findSSRWithPurchasePriority(
        option.ssrs,
        priorityBoardingCodes,
        priorityBoardingCode,
      );

      return ssr?.inBundle === true || ssr?.isConfirmed === true;
    });
  }, [options, priorityBoardingCodes, priorityBoardingCode, direction]);

  useEffect(() => {
    Object.values(options).forEach((option) => {
      const optionKey = `${option.passengerKey}_${direction}`;
      if (addedPriorityBoarding[optionKey]) return;

      const isPending = (loadingKeys ?? []).some((key) => key.startsWith(`${optionKey}_`));
      if (isPending) return;

      const ssr = findSSRWithPurchasePriority(
        option.ssrs,
        priorityBoardingCodes,
        priorityBoardingCode,
      );

      const basePrice = Number(ssr?.price ?? 0);

      if ((ssr?.inBundle || ssr?.isConfirmed) && ssr?.ssrKey) {
        dispatch(
          setPriorityBoardingButtonStatus({
            isAdded: true,
            price: basePrice,
            direction,
            passengerId: option.passengerKey,
            cartType: isCheckIn ? "checkInCart" : "bookingCart",
            alreadyAdded: true,
            ssrKey: ssr.ssrKey,
            ssrCode: ssr.ssrCode,
          }),
        );
      }
    });
  }, [
    dispatch,
    options,
    direction,
    priorityBoardingCodes,
    priorityBoardingCode,
    isCheckIn,
    addedPriorityBoarding,
    loadingKeys,
  ]);

  return (
    <FlightSegmentCard
      bundleCode={bundleCode}
      direction={direction}
      directionLabel={directionLabel}
      disabled={isJourneyDisabled}
      InBundleInAnyJourney={isIncludedByBundle}
      cmsData={cmsData}
    >
      {Object.values(options).map((option, index) => {
        const ssr = findSSRWithPurchasePriority(
          option.ssrs,
          priorityBoardingCodes,
          priorityBoardingCode,
        );

        const basePrice = Number(ssr?.price ?? 0);

        const includedInPurchase = isSsrIncluded(
          passengers,
          option.passengerKey,
          SSR_CODES.PRIORITY_BOARDING,
          direction,
        );

        const isLockedBySsr = ssr?.inBundle || ssr?.isConfirmed;

        const isIncluded = includedInPurchase || isLockedBySsr;

        const isDisabled = isJourneyDisabled || isIncluded;

        const optionKey = `${option.passengerKey}_${direction}`;

        return (
          <PassengerItemCard
            key={option.passengerKey}
            index={index}
            passengerKey={option.passengerKey}
            disabled={isDisabled}
            isAdded={!!addedPriorityBoarding[optionKey]}
            price={basePrice}
            onChangeAction={onChangeAction}
            cmsData={cmsData}
            commonInfo={commonInfo}
            ssrKey={ssr?.ssrKey}
            ssrCode={ssr?.ssrCode}
            isInCludedInBundle={isIncluded}
            loading={
              loadingKeys?.includes(`${optionKey}_add`) ||
              loadingKeys?.includes(`${optionKey}_remove`)
            }
          />
        );
      })}
    </FlightSegmentCard>
  );
}
